/**
 * Die Verwaltung: Endpunkte, Basismodelle, Agenten — die Konfiguration, an der
 * die Messgrundlage des ganzen Systems hängt — dazu Projekte, Personen,
 * SVN-Kennungen, das Ticketsystem, die Kontrollmuster und die Prüfaufträge, die
 * liegengeblieben sind.
 *
 * Sichtbar ist sie nur für die Rolle `admin`. Das ist hier eine Frage der
 * Anzeige; **entschieden wird sie im Server**, bei jeder Anfrage. Eine Prüfung,
 * die nur die Oberfläche einhält, ist keine.
 *
 * **Die Bauform: Ansicht mit eigener Sektionsnavigation** (#41). Der Wechsel des
 * Abschnitts wirkt auf den Screen und ist keine Auswahl innerhalb einer
 * Liste — er steht deshalb in der **Werkzeugleiste** und nicht im Inhalt. Eine
 * Registerleiste, die mit der Tabelle wegscrollt, ist die Navigation, die man
 * beim Lesen verliert; die Leiste schwebt darüber.
 *
 * **Einstufig in der Brotkrume.** Ein Abschnitt ist kein Ort: Er hat keine
 * Adresse, kein „zurück" führt zu ihm, und er steht schon in der Leiste. Ein
 * zweites Glied „Verwaltung ▸ Endpunkte" wäre dieselbe Auskunft zweimal.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { TabStrip } from "../../design-system";
import type { AdminApi } from "../../api/client";
import { Toolbar } from "../../shell/Toolbar";
import { ListPage } from "../list";
import { AgentsSection } from "./AgentsSection";
import { BaseModelsSection } from "./BaseModelsSection";
import { ControlPatternsSection } from "./ControlPatternsSection";
import { EndpointsSection } from "./EndpointsSection";
import { IssueTrackerSection } from "./IssueTrackerSection";
import { PeopleSection } from "./PeopleSection";
import { ProjectsSection } from "./ProjectsSection";
import { QueueLoadSection } from "./QueueLoadSection";
import { ReviewJobsSection } from "./ReviewJobsSection";
import { SvnIdentitiesSection } from "./SvnIdentitiesSection";

type Section =
  | "endpoints"
  | "base-models"
  | "agents"
  | "projects"
  | "people"
  | "svn-identities"
  | "issue-tracker"
  | "control-patterns"
  | "review-jobs";

const SECTIONS: { id: Section; label: string }[] = [
  { id: "endpoints", label: "Endpunkte" },
  { id: "base-models", label: "Basismodelle" },
  { id: "agents", label: "Agenten" },
  { id: "projects", label: "Projekte" },
  { id: "people", label: "Personen" },
  { id: "svn-identities", label: "SVN-Kennungen" },
  { id: "issue-tracker", label: "Ticketsystem" },
  { id: "control-patterns", label: "Kontrollmuster" },
  { id: "review-jobs", label: "Prüfaufträge" },
];

/**
 * Die Beschriftungen in der Reihenfolge, in der sie in der Leiste stehen.
 *
 * `TabStrip` wählt über die **Beschriftung** und nicht über eine Kennung: Der
 * Baustein des Skills kennt keine Kennung neben dem Text. Die Kennung bleibt
 * trotzdem der Zustand dieser Ansicht — sie ist englisch und übersteht eine
 * Umformulierung der Beschriftung (ADR-0009).
 */
const LABELS = SECTIONS.map((one) => one.label);

export function AdminView({ client }: { client: AdminApi }): ReactElement {
  const [section, setSection] = useState<Section>("endpoints");
  const shown = SECTIONS.find((one) => one.id === section) as (typeof SECTIONS)[number];

  return (
    <>
      <Toolbar>
        <TabStrip
          tabs={LABELS}
          value={shown.label}
          onSelect={(label) => {
            const picked = SECTIONS.find((one) => one.label === label);

            if (picked !== undefined) {
              setSection(picked.id);
            }
          }}
          style={{ flex: 1, minWidth: 0 }}
        />
      </Toolbar>

      <ListPage>
        {/*
          Der oberste Abschnitt jeder Registerkarte trägt `first`: Die Leiste
          darüber schließt mit einer Linie in `--line-f`, und eine zweite
          unmittelbar darunter wäre eine doppelte.
        */}
        {section === "endpoints" ? <EndpointsSection client={client} /> : null}
        {section === "base-models" ? <BaseModelsSection client={client} /> : null}
        {section === "agents" ? <AgentsSection client={client} /> : null}
        {section === "projects" ? <ProjectsSection client={client} /> : null}
        {section === "people" ? <PeopleSection client={client} /> : null}
        {section === "svn-identities" ? <SvnIdentitiesSection client={client} /> : null}
        {section === "issue-tracker" ? <IssueTrackerSection client={client} /> : null}
        {section === "control-patterns" ? <ControlPatternsSection client={client} /> : null}
        {/*
          Dieselbe Registerkarte, weil es dieselbe Frage in zwei Schärfen ist: kommt
          das System hinterher — und was ist steckengeblieben? Der Stand steht oben,
          weil er die Lage beschreibt, und die Störungen darunter, weil sie ihre
          Ursache benennen.
        */}
        {section === "review-jobs" ? (
          <>
            <QueueLoadSection client={client} />
            <ReviewJobsSection client={client} />
          </>
        ) : null}
      </ListPage>
    </>
  );
}
